import { useContext, useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, LegendPayload } from 'recharts';
import EventsService from "../../services/EventsService";
import { useUserStore } from "../../contexts/userStore";
import { useTheme } from "../../contexts/theme";
import { getTimezoneOffsetInHours } from "../../utils/date";
import { useSSEContext } from "../../contexts/sse";
import { clamp } from "../../utils/math";
const chartMargin = { top: 5, right: 20, left: -10, bottom: 5 };
const tooltipCursorStyle = { fill: 'rgba(128, 128, 128, 0.1)' };
const legendWrapperStyle = { fontSize: '14px', left: 10, bottom: '-10px' };
const barRadius = [4, 4, 0, 0];

export const CustomTooltip = ({ active, payload, label }) => {
   const { themeName } = useTheme();
   const tooltipStyle = useMemo(
      () => ({
         backgroundColor: `${themeName?.name === 'black' ? '#15191dff' : 'rgb(241, 239, 239)'}`,
         border: `1px solid var(--borderColor)`,
         borderRadius: '0.5rem',
         padding: '0.5rem 1rem',
         boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      }),
      [themeName]
   );

   if (active && payload && payload.length) {
      return (
         <div style={tooltipStyle} className="text-sm">
            <p className="label font-bold mb-1! text-(--textColor)">{`${label}`}</p>
            {payload.map((pld, index) => {
               return <p key={index} style={{ color: pld.color }} className="mb-0.5!">{`${pld.name}: ${pld.value}`}</p>
            })}
         </div>
      );
   }
   return null;
};


export const AccessEventsFlowChart = ({ showLegend = false, showYTexts = false, listMode = "all", ...props }) => {
   const { themeName } = useTheme();
   const [accessEventsFlowData, setAccessEventsFlowData] = useState([]);
   const [showEntries, setShowEntries] = useState(true);
   const [showExits, setShowExits] = useState(true);
   const { selectedCondominio } = useUserStore();
   const { sseReceived } = useSSEContext();
   const [isLoading, setIsLoading] = useState(true);
   const tzOffset = getTimezoneOffsetInHours();

   function organizeByHours(data) {
      const organizedData = [...data];
      const minHour = organizedData.length > 0 ? Math.min(...organizedData.map((item) => item.hour)) : 0;
      const maxHour = organizedData.length > 0 ? Math.max(...organizedData.map((item) => item.hour)) : 0;
      const currentHour = new Date().getUTCHours();
      const maxHourToDisplay = Math.max(maxHour, currentHour);
      const minHourToDisplay = minHour - maxHourToDisplay > 4 ? minHour : Math.max(0, maxHourToDisplay - 4);

      for (let hour = minHourToDisplay; hour <= maxHourToDisplay; hour++) {
         if (!organizedData.some((item) => item.hour === hour)) {
            organizedData.push({ hour, entries: 0, exits: 0 });
         }
      }

      organizedData.sort((a, b) => a.hour - b.hour);

      if (organizedData.length > 0) {
         const firstHour = organizedData[0].hour;
         const firstHourEntries = organizedData[0].entries;
         const firstHourExits = organizedData[0].exits;
         if (firstHour > 0 && (firstHourEntries > 0 || firstHourExits > 0)) {
            return [
               {
                  hour: firstHour - 1,
                  entries: 0,
                  exits: 0,
               },
               ...organizedData,
            ]
         }
      }

      return organizedData;
   }

   function updateAccessEventsFlowData(data = null) {
      setAccessEventsFlowData((prevData) => {
         const _data = data || prevData;
         if (!_data || !Array.isArray(_data)) {
            return [];
         }
         return organizeByHours(_data).filter(item => (item.hour + tzOffset) > 0);
      });
   }

   async function getTodayAccessEventsByHour() {
      setIsLoading(true);
      let response = null;
      try {
         response = await EventsService.getTodayAccessEventsByHour(selectedCondominio, listMode);
      } catch (error) {
         console.error('Error fetching access events flow data:', error);
      }
      setIsLoading(false);
      return response;
   }

   async function fetchAccessEventsFlowData() {
      try {
         const { data } = await getTodayAccessEventsByHour();
         updateAccessEventsFlowData(data);
      } catch (error) {
         console.error('Error fetching access events flow data:', error);
      }
   }

   useEffect(() => {
      if (sseReceived?.action === "createAccessEvent") {
         fetchAccessEventsFlowData();
      }
   }, [sseReceived]);

   useEffect(() => {
      fetchAccessEventsFlowData();

      // let intervalId;
      // const minutesUntilNextHour = 60 - new Date().getUTCMinutes();

      // const timeout = setTimeout(() => {
      //    updateAccessEventsFlowData();
      //    intervalId = setInterval(() => {
      //       updateAccessEventsFlowData();
      //    }, 60 * 60 * 1000);
      // }, minutesUntilNextHour * 60 * 1000);

      // return () => {
      //    clearInterval(intervalId);
      //    clearTimeout(timeout);
      // }
   }, [listMode, selectedCondominio]);

   const axisTickStyle = useMemo(() => ({ fill: themeName?.name === 'black' ? 'rgb(156 163 175)' : 'rgb(107 114 128)', fontSize: 12 }), [themeName]);
   const gridStroke = useMemo(() => (themeName?.name === 'black' ? 'rgba(55, 65, 81, 0.5)' : 'rgba(229, 231, 235, 1)'), [themeName]);

   function handleLegendClick(e) {
      if (e.dataKey === 'entries') {
         setShowEntries(prev => !prev);
      }
      if (e.dataKey === 'exits') {
         setShowExits(prev => !prev);
      }
   }

   return (
      <div className="w-full flex flex-col rounded-xl border border-border-light dark:border-border-dark h-[194px]">
         {isLoading ? (
            <div className="size-full shimmer"></div>
         ) : (
            <div className={`${showYTexts ? 'w-[calc(100%+36px)] -ml-9.5!' : 'w-[calc(100%+50px)] [&_text[orientation=left]]:opacity-0 -ml-14!'} flex-1 pt-5! ${showLegend ? 'pb-6!' : 'pb-2!'} pl-5! pr-5! **:outline-none! box-border!`}>
               <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                     accessibilityLayer
                     data={accessEventsFlowData.map(item => ({ ...item, name: `${item.hour + tzOffset}h`, hour: item.hour + tzOffset }))}
                     margin={{
                        left: 0,
                        right: 0,
                     }}
                  >
                     <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" horizontal={true} vertical={false} />
                     <YAxis tick={axisTickStyle} tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} />
                     <XAxis dataKey="name" tick={axisTickStyle} tickLine={true} axisLine={false} tickMargin={8} />
                     <Tooltip content={<CustomTooltip />} cursor={tooltipCursorStyle} />
                     <defs>
                        <linearGradient id="fillEntries" x1="0" y1="0" x2="0" y2="1">
                           <stop
                              offset="14%"
                              stopColor="var(--entryColor)"
                              stopOpacity={0.9}
                           />
                           <stop
                              offset="86%"
                              stopColor="var(--entryColor)"
                              stopOpacity={0.1}
                           />
                        </linearGradient>
                        <linearGradient id="fillExits" x1="0" y1="0" x2="0" y2="1">
                           <stop
                              offset="14%"
                              stopColor="var(--exitColor)"
                              stopOpacity={0.9}
                           />
                           <stop
                              offset="86%"
                              stopColor="var(--exitColor)"
                              stopOpacity={0.1}
                           />
                        </linearGradient>
                     </defs>
                     <Area
                        name="Entradas"
                        dataKey="entries"
                        type="linear"
                        fill="url(#fillEntries)"
                        fillOpacity={0.2}
                        stroke="var(--entryColor)"
                        strokeWidth={2}
                        hide={!showEntries}
                     />
                     <Area
                        name="Saídas"
                        dataKey="exits"
                        type="linear"
                        fill="url(#fillExits)"
                        fillOpacity={0.2}
                        stroke="var(--exitColor)"
                        strokeWidth={2}
                        hide={!showExits}
                     />
                     {showLegend && (
                        <Legend wrapperStyle={legendWrapperStyle} onClick={handleLegendClick} />
                     )}
                  </AreaChart>
               </ResponsiveContainer>
            </div>
         )}
      </div>
   );
};
